package com.milta.feature.optimizationquota.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.core.network.ApiException
import com.milta.feature.optimizationquota.domain.entity.OptimizationLimits
import com.milta.feature.optimizationquota.domain.usecase.GetOptimizationLimitsUseCase
import com.milta.feature.optimizationquota.domain.usecase.UpdateOptimizationLimitsUseCase
import kotlinx.coroutines.launch

/** The 4 fields of the "Optimisations RoutaR" tab (R4). */
enum class OptimizationLimitField { EnterpriseTrial, EnterprisePaid, DriverTrial, DriverPaid }

/** Per-field validation error, resolved to a localized message by the screen. */
enum class OptimizationLimitFieldError { Required, MustBePositive }

data class AdminUsageLimitsState(
    val isLoading: Boolean = true,
    /** "server_error" | "offline" — the limits could not be read at all. */
    val loadError: String? = null,
    val values: Map<OptimizationLimitField, String> = emptyMap(),
    val fieldErrors: Map<OptimizationLimitField, OptimizationLimitFieldError> = emptyMap(),
    val isSaving: Boolean = false,
    /** Non-null right after a successful save — drives the "Limites enregistrées" banner. */
    val savedLimits: OptimizationLimits? = null,
    /** "validation" (server 400) | "server_error" | "offline". */
    val saveError: String? = null,
)

sealed interface AdminUsageLimitsIntent {
    data object Load : AdminUsageLimitsIntent
    data class UpdateField(val field: OptimizationLimitField, val value: String) : AdminUsageLimitsIntent
    data object Save : AdminUsageLimitsIntent
}

/** MIL-97 — Admin "Limites d'usage", tab "Optimisations RoutaR" (A1). Web-only. */
class AdminUsageLimitsViewModel(
    private val getLimits: GetOptimizationLimitsUseCase,
    private val updateLimits: UpdateOptimizationLimitsUseCase,
) : MviViewModel<AdminUsageLimitsState, AdminUsageLimitsIntent>(AdminUsageLimitsState()) {

    override fun onIntent(intent: AdminUsageLimitsIntent) {
        when (intent) {
            AdminUsageLimitsIntent.Load -> load()
            is AdminUsageLimitsIntent.UpdateField -> setState {
                copy(
                    // Capped in length: an oversized entry never overflows Int (→ a false "> 0" error).
                    values = values + (intent.field to intent.value.filter { it.isDigit() }.take(MAX_INPUT_DIGITS)),
                    fieldErrors = fieldErrors - intent.field,
                    savedLimits = null,
                    saveError = null,
                )
            }
            AdminUsageLimitsIntent.Save -> save()
        }
    }

    private fun load() {
        setState { copy(isLoading = true, loadError = null) }
        scope.launch {
            getLimits().fold(
                onSuccess = { limits -> setState { copy(isLoading = false, values = limits.toFields()) } },
                onFailure = { e -> setState { copy(isLoading = false, loadError = errorCode(e)) } },
            )
        }
    }

    private fun save() {
        if (currentState.isSaving) return
        val errors = OptimizationLimitField.entries.mapNotNull { field ->
            val value = currentState.values[field].orEmpty()
            when {
                value.isBlank() -> field to OptimizationLimitFieldError.Required
                (value.toIntOrNull() ?: 0) <= 0 -> field to OptimizationLimitFieldError.MustBePositive
                else -> null
            }
        }.toMap()
        if (errors.isNotEmpty()) {
            setState { copy(fieldErrors = errors, savedLimits = null) }
            return
        }
        val values = currentState.values
        val limits = OptimizationLimits(
            enterpriseTrial = values.getValue(OptimizationLimitField.EnterpriseTrial).toInt(),
            enterprisePaid = values.getValue(OptimizationLimitField.EnterprisePaid).toInt(),
            driverTrial = values.getValue(OptimizationLimitField.DriverTrial).toInt(),
            driverPaid = values.getValue(OptimizationLimitField.DriverPaid).toInt(),
        )
        setState { copy(isSaving = true, saveError = null, savedLimits = null) }
        scope.launch {
            updateLimits(limits).fold(
                onSuccess = { saved -> setState { copy(isSaving = false, savedLimits = saved, values = saved.toFields()) } },
                onFailure = { e ->
                    // 400 error.validation.{enterprise_trial|enterprise_paid|driver_trial|driver_paid}:
                    // shown under the field concerned, the generic message only if none matches.
                    val field = (e as? ApiException)?.takeIf { it.statusCode == HTTP_BAD_REQUEST }
                        ?.let { error -> FIELD_ERROR_CODES.entries.firstOrNull { error.message == it.value }?.key }
                    val code = if (e is ApiException && e.statusCode == HTTP_BAD_REQUEST) "validation" else errorCode(e)
                    setState {
                        copy(
                            isSaving = false,
                            saveError = code.takeIf { field == null },
                            fieldErrors = if (field != null) fieldErrors + (field to OptimizationLimitFieldError.MustBePositive) else fieldErrors,
                        )
                    }
                },
            )
        }
    }

    private fun OptimizationLimits.toFields() = mapOf(
        OptimizationLimitField.EnterpriseTrial to enterpriseTrial.toString(),
        OptimizationLimitField.EnterprisePaid to enterprisePaid.toString(),
        OptimizationLimitField.DriverTrial to driverTrial.toString(),
        OptimizationLimitField.DriverPaid to driverPaid.toString(),
    )

    private fun errorCode(e: Throwable) = if (e is ApiException) "server_error" else "offline"

    private companion object {
        const val HTTP_BAD_REQUEST = 400
        val FIELD_ERROR_CODES = mapOf(
            OptimizationLimitField.EnterpriseTrial to "error.validation.enterprise_trial",
            OptimizationLimitField.EnterprisePaid to "error.validation.enterprise_paid",
            OptimizationLimitField.DriverTrial to "error.validation.driver_trial",
            OptimizationLimitField.DriverPaid to "error.validation.driver_paid",
        )
    }
}
